import React, { useContext, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import "./CityPage.scss";

import Map from "../../components/map/Map";
import Back from "../../components/back-button/Back";
import Compass from "../../components/compass/Compass";
import Loader from "../../components/loader-spinner/Loader";

import { MainContext } from "../../contexts/MainContext";

import Project from "../../components/map/markers/Project";
import Facility from "../../components/map/markers/Facility";
import GroupMarker from "../../components/map/markers/GroupMarker";

import GroupModal from "../../components/group-modal/GroupModal";

export default function CityPage() {
    const { pathname } = useLocation();
    const { cityName } = useParams();

    const {
        selectedItems,
        communitiesMarkers,
        setCommunitiesMarkers,
        groupModal,
        setGroupModal,
        facilityMarkers,
        allCommunities,
        allProperties,
        allGroups,
        loading,
        pages,
    } = useContext(MainContext);

    // Debug the communities on component mount
    useEffect(() => {
        console.log("All communities:", allCommunities);
        console.log("All properties:", allProperties);
    }, [allCommunities, allProperties]);

    const city = pages.find((city) => {
        // Remove leading slash from pathname for comparison
        const currentPath = pathname.replace(/^\//, "");
        return currentPath === city.path.replace(/^\//, "");
    });

    // Show loader for both initial loading and when city is not found
    if (loading || !city) {
        return <Loader isVisible={true} />;
    }

    // Check if map data is available
    if (!city.map) {
        console.error(
            `Map data missing for city: ${city.name} (ID: ${city.id})`
        );
        return (
            <div className="error-container">
                <h2>Map Unavailable</h2>
                <p>The map for {city.name} is currently unavailable.</p>
                <Back link={"/"} />
            </div>
        );
    }

    const filteredMarkers = facilityMarkers.map(({ id, markers }) => ({
        id,
        markers: Array.isArray(markers)
            ? markers.filter(
                  (marker) => marker && marker.city_path === "/" + cityName
              )
            : [],
    }));

    const groupModalHandler = (groupMarker) => {
        setGroupModal(groupMarker);
    };

    const getCityMap = () => {
        return city?.map || null;
    };

    const getCityMapMin = () => {
        const cityMap = getCityMap();
        return cityMap ? cityMap.replace(".jpg", "-min.jpg") : null;
    };

    const getCityId = () => {
        // Return the proper map name instead of numeric ID
        // Extract from path to ensure consistent naming with backend
        if (city?.path) {
            // Remove leading slash and return the path (e.g., "dubai", "ras-al-khaimah")
            return city.path.replace(/^\//, "");
        }
        return "";
    };

    const getCommunity = () => {
        return allCommunities.filter(
            (community) => community.city_path === cityName
        );
    };

    const getProperty = () => {
        // First filter properties by city path and enabled status
        const properties = allProperties.filter(
            (property) =>
                // Filter by city path and ensure property is enabled (backward compatible with properties without enabled flag)
                property.city_path === cityName &&
                (property.enabled === undefined || property.enabled === true)
        );

        // Modify each property to set bird=null if its community doesn't exist
        return properties.map((property) => {
            const hasValidCommunity = checkCommunityExists(property);
            console.log(
                `Property ${property.name}, path: ${property.path}, has valid community: ${hasValidCommunity}`
            );

            if (!hasValidCommunity) {
                // Return a new object with bird set to null
                return {
                    ...property,
                    bird: null, // This will make it non-clickable in both MarkersMapper and Project components
                };
            }
            return property;
        });
    };

    // Function to check if a community exists for a property
    const checkCommunityExists = (property) => {
        if (!property || !property.path) return false;

        // For a path like "/dubai/al-jaddaf/art-bay", we need to check if "/dubai/al-jaddaf" exists
        const pathParts = property.path.split("/");

        // Need at least 3 parts: ["", "dubai", "al-jaddaf", "art-bay"]
        if (pathParts.length < 4) return false;

        // Construct the community path: /dubai/al-jaddaf
        const communityPath = `/${pathParts[1]}/${pathParts[2]}`;

        // Find the community
        const community = allCommunities.find(
            (community) => community.path === communityPath
        );

        // Check if community exists AND has a valid bird property
        const communityValid = community && community.bird;

        console.log(
            `Property: ${
                property.name
            }, community path: ${communityPath}, found: ${!!community}, has bird: ${!!community?.bird}, valid: ${communityValid}`
        );

        return communityValid;
    };

    const getGroupMarkers = () => {
        return allGroups.filter((group) => group.city_path === cityName);
    };

    const getDefaultPosition = () => {
        if (cityName === "dubai") return { x: 3470, y: 300 };
        if (cityName === "ras-al-khaimah") return { x: 252, y: 1300 };
        if (cityName === "abu-dhabi") return { x: 0, y: 0 };
        return { x: 0, y: 0 };
    };

    const getTargetZoom = () => {
        if (cityName === "dubai") return 1.4;
        if (cityName === "ras-al-khaimah") return 1.4;
        if (cityName === "abu-dhabi") return 1.4;
        return 1.4;
    };

    const getTargetPosition = () => {
        if (cityName === "dubai") return { x: 3515, y: 0 };
        if (cityName === "ras-al-khaimah") return { x: 1551, y: 1340 };
        if (cityName === "abu-dhabi") return { x: 0, y: 0 };
        return { x: 0, y: 0 };
    };

    return (
        <Map
            name={getCityId()}
            image={getCityMap()}
            imageMin={getCityMapMin()}
            naturalSize={{ width: 6000, height: 3375 }}
            defaultPosition={getDefaultPosition()}
            targetZoom={getTargetZoom()}
            targetPosition={getTargetPosition()}
        >
            <Back link={"/"} />
            <Compass />
            <Map.VisibilitySelect />
            <Map.DistancesSwitch />
            <Map.ZoomButton />
            <Map.MarkersPath />
            <Map.MarkersPathLoader />
            <Map.MarkersPathWindow />

            {communitiesMarkers ? (
                <Map.MarkersMapper
                    markers={getCommunity()}
                    MarkerComponent={Project}
                />
            ) : (
                <Map.MarkersMapper
                    markers={getProperty()}
                    MarkerComponent={Project}
                />
            )}

            {!communitiesMarkers && (
                <Map.MarkersMapper
                    markers={getGroupMarkers()}
                    MarkerComponent={GroupMarker}
                    onClick={groupModalHandler}
                />
            )}

            {groupModal && (
                <GroupModal
                    closeFunction={() => setGroupModal(null)}
                    groupModal={groupModal}
                />
            )}

            {filteredMarkers.map(
                ({ id, markers }) =>
                    selectedItems.includes(id) && (
                        <Map.MarkersMapper
                            key={id}
                            markers={markers}
                            MarkerComponent={Facility}
                        />
                    )
            )}

            <Map.Clouds />
        </Map>
    );
}
